import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

import { CheckProps } from './Check.types'
import { Badge, Flex, CardContent, Typography, ButtonText, Loader } from '@percent/lemonade'
import { statusMap } from '@percent/admin-dashboard/constants/statusMap'
import {
  CheckType,
  ProfileDataCheckReasons,
  MonetaryVerificationCheckStatus,
  type ProfileDataCheckReason,
  MonetaryVerificationStatus
} from '@percent/admin-dashboard/api/actions/monetaryVerifications/monetaryVerifications.types'

export function Check({
  check,
  monetaryVerification,
  onCheckToSubmit,
  title,
  isLoading,
  isError
}: Readonly<CheckProps>) {
  const { t } = useTranslation()

  const openInNewTab = (url: string) => window.open(url, '_blank')

  const action = useMemo(() => {
    if (!check) {
      return null
    }

    if (check?.type === CheckType.VALIDATION_SUBMISSION) {
      return (
        <ButtonText
          onClick={() => openInNewTab(`/validation-submissions/${monetaryVerification.validationSubmissionId}`)}
          icon="expand"
        >
          {t('typography.goToCheck')}
        </ButtonText>
      )
    }

    if (monetaryVerification.status === MonetaryVerificationStatus.EXPIRED) {
      return null
    }

    return (
      <ButtonText onClick={() => onCheckToSubmit?.(check)}>
        {check?.status === MonetaryVerificationCheckStatus.PENDING
          ? t('typography.submitCheck')
          : t('typography.reSubmitCheck')}
      </ButtonText>
    )
  }, [check, t, monetaryVerification.validationSubmissionId, onCheckToSubmit])

  const reason = useMemo(() => {
    if (!check?.reason) {
      return null
    }

    const checkReason = check.reason as ProfileDataCheckReason

    if (Object.values(ProfileDataCheckReasons).includes(checkReason)) {
      return <Typography>{t(`monetaryDonationVerificationRejectionReasons.${checkReason}`)}</Typography>
    }

    return <Typography>{check.reason}</Typography>
  }, [check?.reason, t])

  return (
    <CardContent title={title} data-testid={`check-${check?.type}-${check?.status}`}>
      <Flex justify="space-between" align="center">
        <Flex gap={8} align="center">
          {isLoading && <Loader isFullScreen={false} />}
          {!isLoading && isError && <Typography $color="alert400">{t('typography.couldntLoadCheckData')}</Typography>}
          {!isLoading && check && (
            <>
              <Badge variant={statusMap[check.status].variant} icon={statusMap[check.status].icon}>
                {t(statusMap[check.status].text)}
              </Badge>
              {reason}
            </>
          )}
        </Flex>
        {!isLoading && check && action}
      </Flex>
    </CardContent>
  )
}
